import React, { useCallback } from 'react';

// DEPENDENCIES
import clsx from 'clsx';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { apiFetch, makePath, documentFetch, fileDownload, formatDateTime } from 'functions';

// HELPERS
import { useRecordContext } from 'helpers/getRecordData';

// REACT BOOTSTRAP COMPONENTS
import { Col, Row } from 'react-bootstrap';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import VehicleManagementItemComments from './VehicleManagementItemComments';

// STYLE
import styles from './vehicleManagement.module.scss';

// MAIN COMPONENT
const VehicleManagementItemStatus = ({
  title,
  options,
  history,
  inventoryId,
  trackingCodeId,
  trackingTypeId,
  trackingType,
  trackingCodeName,
  trackMiscTaskId,
  trackMiscTaskName,
  downloadFile
}) => {

  const { refetchRecord } = useRecordContext();

  const updateTrackingCodeStatus = ({ target }) =>
    apiFetch({
      endpoint: trackMiscTaskId
        ? ENDPOINTS.inventory.miscTask.updateTask(inventoryId, trackMiscTaskId)
        : makePath(ENDPOINTS.inventory.updateTracking, inventoryId),
      method: 'PUT',
      params: trackMiscTaskId
        ? {
            tracking_misc_task_name: trackMiscTaskName,
            tracking_code_id: parseInt(target.value, 10)
          }
        : {
            trackName: trackingType,
            statusId: parseInt(target.value, 10)
          },
      loadingMessage: `Changing ${trackMiscTaskName || title}'s status`,
      successMessage: 'Status changed successfully',
      onSuccess: () => {
        refetchRecord();
      }
    });

  const statusValue = 
  !!history.length
    ? `${history[0].tracking_code_name} ${
        history[0].tracking_code_date
          ? `@ ${formatDateTime(history[0].tracking_code_date)}`
          : ''
      } ${history[0].tracking_code_user ? `By ${history[0].tracking_code_user}` : ''}`
    : trackingCodeName;

  // CALLBACKS
  const DownloadButton = useCallback(
    ({ label, type, status }) => {
      const endpoint =
        type === 'detailCleanUp' && status === 'Request / Pending Detail' ? ENDPOINTS.print.inventory.requestDetail :
        type === 'detailCleanUp' && status === 'Request / Pending Clean Up' ? ENDPOINTS.print.inventory.requestCleanup :
        type === 'inspection' && status === 'Request / Pending Inspection' ? ENDPOINTS.print.inventory.inspectionForm : undefined;
      const fileName =
        type === 'detailCleanUp' ? (status === 'Request / Pending Detail' ? 'detail' : 'clean_up') : type;
      const args = {
        endpoint: makePath(endpoint, inventoryId),
        accept: 'pdf',
        contentType: 'pdf',
        mimeType: 'application/pdf',
        loadingMessage: `Downloading ${status} Form`,
        errorMessage: `Unable to download ${status} Form`,
        successMessage: `${status} Form downloaded.`,
        onSuccess: ({ url }) => {
          fileDownload(url, `${downloadFile}_${fileName}_authorization`, false, true, true);
        }
      };
      return !endpoint ? null : (
        <Button.Link
          label={label || 'Download Form'}
          onClick={() => documentFetch(args)}
        />
      )
    },
    [inventoryId, downloadFile]
  )
  const DownloadLabel = useCallback(
    ({ title, label }) => {
      const status = label;
      const type = title === 'Detail & Clean Up' ? 'detailCleanUp' : title === 'Inspection' ? 'inspection' : undefined;
      return <DownloadButton  {...{label, status, type}} />;
    },
    []  
  )
  const TableRow = useCallback(
    ({ id, createdAt, user, type, status, isHead = !id }) => (
      <Row className={clsx(styles.row, isHead && styles.head)}>
        <Col className={clsx(styles.col, styles.id)}>{isHead ? '#' : id}</Col>
        <Col className={styles.col}>{isHead ? 'Status' : status}</Col>
        <Col className={styles.col}>{!isHead &&
          <DownloadButton  {...{status, type}} />
        }</Col>
        <Col className={styles.col}>{isHead ? 'Date' : formatDateTime(createdAt) || '-'}</Col>
        <Col className={styles.col}>{isHead ? 'User' : user || '-'}</Col>
      </Row>
    ),
    []
  )

  // RENDER
  return (
    <Form.Body className={styles.formBody}>
      {options && (
        <Form.Checklist
          type="radio"
          label={trackMiscTaskName || title}
          value={trackingCodeId}
          options={options}
          optionsMapper={options =>
            options.map(o => ({
              label:
                [10, 11, 133].includes(o.value) && parseInt(trackingCodeId) === o.value ? (
                  <DownloadLabel
                    className={styles.labelButton}
                    label={o.label}
                    title={title}
                  />
                ) : (
                  <span>{o.label}</span>
                ),
              value: o.value
            }))
          }
          onChange={updateTrackingCodeStatus}
          required
          fullWidth
          list={{
            direction: 'horizontal',
            justify: 'start'
          }}
          useFormik={false}
        />
      )}
      <Form.PlainText
        label="Status"
        value={statusValue}
        fullWidth
      />
      {!!history?.length &&
        <Form.Col>
          <div className={styles.table}>
            <TableRow isHead={true} />
            {history.map(
              ({ tracking_type, tracking_code_date, tracking_code_name, tracking_code_user }, index) => (
                <TableRow
                  key={`${tracking_type}-${index}`}
                  type={tracking_type}
                  user={tracking_code_user}
                  createdAt={tracking_code_date}
                  status={tracking_code_name}
                  id={index + 1}
                />
              )
            )}
          </div>
        </Form.Col>
      }
      <VehicleManagementItemComments
        inventoryId={inventoryId}
        trackingTypeId={trackingTypeId}
        trackMiscId={trackMiscTaskId}
      />
    </Form.Body>
  );
};

// EXPORT
export default VehicleManagementItemStatus;
